import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { generateObjectId } from '@builttocreate/engine-utils';
import { 
  getCurrentFieldExpression, 
  getFieldFormulaId, 
  formatValue,
  getFieldWarnings,
  getFieldChanges
} from './modalHelpers';

// Styled components
const StyledFieldCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 8px;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  }
  
  &.has-warning {
    border-color: #f59e0b;
    background-color: #fffbeb;
  }
  
  &.changed {
    border-color: #10b981;
    background-color: #f0fdf4;
  }
  
  &.has-formula-changes {
    border-color: #8b5cf6;
    background-color: #faf5ff;
  }
  
  &.no-formula {
    opacity: 0.6;
    background-color: #f8fafc;
    border-color: #e5e7eb;
    
    &:hover {
      opacity: 0.8;
      background-color: #f1f5f9;
      border-color: #cbd5e1;
    }
  }
  
  &.no-formula.expanded {
    opacity: 0.8;
    background-color: #f8fafc;
  }
`;

const StyledFieldHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const StyledFieldInfo = styled.div`
  flex: 1;
`;

const StyledFieldId = styled.span`
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 2px;
`;

const StyledFieldTitle = styled.span`
  font-size: 12px;
  color: #64748b;
  padding-left: 4px;
`;

const StyledFormulaId = styled.span`
  font-size: 10px;
  color: #94a3b8;
  margin-left: 8px;
  font-weight: 400;
`;

const StyledBadgeContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledWarningBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background-color: #fef3c7;
  color: #a16207;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
`;

const StyledChangesBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background-color: #ede9fe;
  color: #7c3aed;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
`;

const StyledFieldExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }
`;

const StyledFieldCardContent = styled.div`
  transition: all 0.15s ease;
  overflow: hidden;
  max-height: ${props => props.expanded ? '1000px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  margin-top: ${props => props.expanded ? '12px' : '0'};
`;

const StyledWarningList = styled.div`
  margin-bottom: 12px;
  padding: 10px;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
`;

const StyledWarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledWarningIcon = styled.span`
  color: #f59e0b;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const StyledWarningMessage = styled.div`
  flex: 1;
  font-size: 12px;
  color: #92400e;
  line-height: 1.4;
`;

const StyledWarningType = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #78350f;
  background-color: #fbbf24;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: auto;
  flex-shrink: 0;
`;

const StyledFormulaList = styled.div``;

const StyledFormula = styled.div`
  padding: 0;
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledFormulaTextarea = styled.textarea`
  width: 98%;
  min-height: 50px;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  color: #1e293b;
  background-color: #ffffff;
  resize: vertical;
  transition: all 0.15s ease;
  
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
  
  &.has-changes {
    border-color: #8b5cf6;
    background-color: #fefbff;
  }
`;

const StyledValueComparison = styled.div`
  display: flex;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
  min-height: 32px;
`;

const StyledValue = styled.span`
  text-align: center;
`;

const StyledValueLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const StyledValueContent = styled.span`
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  
  &.before {
    color: #dc2626;
    border-color: #fca5a5;
    background-color: #fef2f2;
  }
  
  &.after {
    color: #059669;
    border-color: #86efac;
    background-color: #f0fdf4;
  }
  
  &.current {
    color: #1e293b;
  }
`;

const StyledArrow = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const StyledRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  margin-left: 8px;
  border: none;
  background-color: ${props => props.confirming ? '#fee2e2' : '#f1f5f9'};
  color: ${props => props.confirming ? '#dc2626' : '#64748b'};
  cursor: pointer;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.15s ease;
  opacity: ${props => props.show ? '1' : '0'};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: ${props => props.show ? 'translateX(0)' : 'translateX(10px)'};
  
  &:hover {
    background-color: ${props => props.confirming ? '#fca5a5' : '#e2e8f0'};
    color: ${props => props.confirming ? '#b91c1c' : '#1e293b'};
  }
  
  &:active {
    transform: ${props => props.show ? 'scale(0.95) translateX(0)' : 'translateX(10px)'};
  }
`;

/**
 * Individual field formula editor component
 * Handles its own state and saves independently
 */
export default function FieldFormulaEditor({
  field,
  beforeDoc,
  isResolved,
  resolvedResult,
  expandedFields,
  onToggleExpand,
  onUnsavedChange,
  onExpressionChange,
  onRemoveFormula
}) {
  // Local state for this field's formula
  const [localExpression, setLocalExpression] = useState('');
  const [originalExpression, setOriginalExpression] = useState('');
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  // Use a ref to store a stable generated ID for new formulas
  const newFormulaIdRef = React.useRef(null);

  // Initialize local state when field or document changes
  useEffect(() => {
    const expression = getCurrentFieldExpression(field, beforeDoc);
    setLocalExpression(expression);
    setOriginalExpression(expression);
    setHasLocalChanges(false);
    setIsConfirmingRemove(false);
    setIsRemoved(false);
    // Reset the new formula ID ref when field changes
    newFormulaIdRef.current = null;
  }, [field._id, beforeDoc]);

  // Get or generate a stable formula ID
  const memoizedFormulaId = React.useMemo(() => {
    const existingFormulaId = getFieldFormulaId(field);
    if (existingFormulaId) {
      return existingFormulaId;
    }
    // For new formulas, generate an ObjectId once and reuse it
    if (!newFormulaIdRef.current) {
      newFormulaIdRef.current = generateObjectId();
    }
    return newFormulaIdRef.current;
  }, [field]); // Recalculate when field object changes

  // Check if expression has changed and notify parent
  useEffect(() => {
    const hasChanges = localExpression !== originalExpression || isRemoved;
    setHasLocalChanges(hasChanges);
    
    // Notify parent component about unsaved changes
    if (onUnsavedChange) {
      onUnsavedChange(field._id, hasChanges);
    }
    
    // Update centralized pending changes
    if (onExpressionChange) {
      if (hasChanges && !isRemoved) {
        const isNewFormula = !field.formulas || field.formulas.length === 0;
        onExpressionChange(field._id, localExpression, isNewFormula, memoizedFormulaId);
      } else if (!hasChanges && !isRemoved) {
        // No changes, remove from pending
        onExpressionChange(field._id, null);
      }
      // Note: if isRemoved is true, the removal is handled by onRemoveFormula callback
    }
  }, [localExpression, originalExpression, field._id, memoizedFormulaId, onUnsavedChange, onExpressionChange, isRemoved]);

  const handleExpressionChange = (value) => {
    setLocalExpression(value);
  };

  const handleRemoveFormula = () => {
    if (!isConfirmingRemove) {
      // First click - enter confirm state
      setIsConfirmingRemove(true);
    } else {
      // Second click - actually remove the formula
      setLocalExpression('');
      setOriginalExpression('');
      setIsRemoved(true);
      if (onRemoveFormula) {
        onRemoveFormula(field._id);
      }
      // Reset states
      setIsConfirmingRemove(false);
    }
  };

  // Remove individual save/cancel handlers since we'll handle this globally

  // Get computed values
  const warnings = getFieldWarnings(field._id, resolvedResult);
  const changes = getFieldChanges(field._id, beforeDoc, resolvedResult);
  const isExpanded = expandedFields.has(field._id);
  const formulaId = getFieldFormulaId(field);
  const hasFormula = !isRemoved && field.formulas && field.formulas.length > 0;
  
  // Get the original field with options from beforeDoc
  const originalField = beforeDoc?.fields?.find(f => f._id === field._id) || field;

  return (
    <StyledFieldCard 
      className={`
        ${warnings.length > 0 ? 'has-warning' : ''} 
        ${changes ? 'changed' : ''} 
        ${hasLocalChanges ? 'has-formula-changes' : ''}
        ${!hasFormula ? 'no-formula' : ''}
        ${isExpanded ? 'expanded' : ''}
      `.trim()}
      onMouseEnter={() => setIsHoveringCard(true)}
      onMouseLeave={() => {
        setIsHoveringCard(false);
        setIsConfirmingRemove(false);
      }}
    >
      <StyledFieldHeader>
        <StyledFieldInfo>
          <StyledFieldId>
            {field._id}
            {field.title && field.title !== field._id && (
              <StyledFieldTitle>{field.title}</StyledFieldTitle>
            )}
          </StyledFieldId>
          {formulaId && <StyledFormulaId>({formulaId})</StyledFormulaId>}
        </StyledFieldInfo>
        
        <StyledBadgeContainer>
          {hasLocalChanges && (
            <StyledChangesBadge>
              Unsaved
            </StyledChangesBadge>
          )}
          
          {warnings.length > 0 && (
            <StyledWarningBadge>
              ⚠ {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
            </StyledWarningBadge>
          )}

          {hasFormula && (
            <StyledRemoveButton
              onClick={handleRemoveFormula}
              show={isHoveringCard}
              confirming={isConfirmingRemove}
              title={isConfirmingRemove ? 'Click again to confirm removal' : 'Remove formula from this field'}
            >
              {isConfirmingRemove ? 'Confirm Removal' : 'Remove Formula'}
            </StyledRemoveButton>
          )}

          <StyledFieldExpandButton
            onClick={() => onToggleExpand(field._id)}
            title={isExpanded ? 'Collapse field' : 'Expand field'}
          >
            {isExpanded ? '▼' : '▶'}
          </StyledFieldExpandButton>
        </StyledBadgeContainer>
      </StyledFieldHeader>

      <StyledFieldCardContent expanded={isExpanded}>
        {warnings.length > 0 && (
          <StyledWarningList>
            {warnings.map((warning, idx) => (
              <StyledWarningItem key={idx}>
                <StyledWarningIcon>⚠</StyledWarningIcon>
                <StyledWarningMessage>{warning.message}</StyledWarningMessage>
                {warning.type && <StyledWarningType>{warning.type}</StyledWarningType>}
              </StyledWarningItem>
            ))}
          </StyledWarningList>
        )}

        {!isRemoved && (
          <StyledFormulaList>
            <StyledFormula>
              <StyledFormulaTextarea
                value={localExpression}
                onChange={(e) => handleExpressionChange(e.target.value)}
                placeholder="Enter formula expression (e.g., field1 + field2)..."
                className={hasLocalChanges ? 'has-changes' : ''}
              />
            </StyledFormula>
          </StyledFormulaList>
        )}

        {/* Value Display without action buttons */}
        {isResolved && changes ? (
          <StyledValueComparison>
            <StyledValue>
              <StyledValueLabel>Before </StyledValueLabel>
              <StyledValueContent className="before">
                {formatValue(changes.beforeValue, originalField, originalField)}
              </StyledValueContent>
            </StyledValue>
            
            <StyledArrow>→</StyledArrow>
            
            <StyledValue>
              <StyledValueLabel>After </StyledValueLabel>
              <StyledValueContent className="after">
                {formatValue(changes.afterValue, originalField, originalField)}
              </StyledValueContent>
            </StyledValue>
          </StyledValueComparison>
        ) : (
          <StyledValueComparison>
            <StyledValue>
              <StyledValueLabel>Current Value </StyledValueLabel>
              <StyledValueContent className="current">
                {formatValue(field.value, originalField, originalField)}
              </StyledValueContent>
            </StyledValue>
          </StyledValueComparison>
        )}
      </StyledFieldCardContent>
    </StyledFieldCard>
  );
}